import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Home, Trophy, TrendingUp, Users, ShoppingBag, Menu, X, Settings, Shield, ChevronDown, LogOut, Gift, LogIn } from 'lucide-react'
import { Button } from './ui/button'
import { useEffect, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useUser } from '../contexts/UserContext'
import { isInstalledApp } from '../lib/platform'
import { storeLink } from '../lib/siteLinks'
import SiteLink from './SiteLink'
import DubLogo from './DubLogo'
import NotificationsDropdown from './NotificationsDropdown'
import { Avatar } from './ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu'

function UserProfileDropdown({ isAdmin, handleLogout, user }) {
  const navigate = useNavigate()

  const displayName = user?.full_name || user?.first_name || user?.email?.split('@')[0] || 'User'

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center space-x-2 rounded-full p-2 transition-[background-color,box-shadow,transform,color] duration-200 ease-[cubic-bezier(0.25,0.1,0.25,1)] hover:text-[var(--color-ink)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
          <Avatar
            size="md"
            fullName={displayName}
            avatarUrl={user?.avatar_url}
            avatarType={user?.avatar_type}
          />
          <ChevronDown className="h-4 w-4 opacity-50" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem onClick={() => navigate('/settings')}>
          <Settings className="h-4 w-4 mr-2" />
          Settings
        </DropdownMenuItem>
        {isAdmin && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/admin/dashboard')}>
              <Shield className="h-4 w-4 mr-2" />
              Admin Dashboard
            </DropdownMenuItem>
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogout}>
          <LogOut className="h-4 w-4 mr-2" />
          Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default function Layout({ children }) {
  const location = useLocation()
  const navigate = useNavigate()
  const { signOut } = useAuth()
  const { user } = useUser()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  
  // Check user role from JWT token
  const isAdmin = user?.role === 'admin'

  // When an admin is inside an admin-only flow, the main nav swaps to a
  // single "Admin Dashboard" link so clicking around doesn't bounce them
  // back into the player app. Admin-only routes:
  //   /admin/**               — dashboard + every admin page
  //   /tournaments/create     — admin-only create flow
  //   /bracket/:id/seed       — admin seed editor
  //   /bracket/:id/picks?entryId=official&mode=editOfficial — admin explicitly
  //     editing official results.
  //   /bracket/:id/picks?mode=adminView — admin "View bracket" entry from
  //     the seed editor; a read-only peek that should NOT drop the admin nav
  //     (only the explicit "User Dashboard" button should leave admin).
  // Both `mode` markers are required: an admin who drifted onto
  // ?entryId=official via the locked-bracket fallback while browsing the
  // USER flow (e.g. clicking a bracket from /tournaments/:id) should keep
  // the player nav, not flip into admin.
  const searchParams = new URLSearchParams(location.search)
  const picksMode = searchParams.get('mode')
  const isOnAdminRoute = isAdmin && (
    location.pathname.startsWith('/admin')
    || location.pathname === '/tournaments/create'
    || /^\/bracket\/[^/]+\/seed$/.test(location.pathname)
    || (
      /^\/bracket\/[^/]+\/picks$/.test(location.pathname)
      && (
        picksMode === 'adminView'
        || (picksMode === 'editOfficial' && searchParams.get('entryId') === 'official')
      )
    )
  )

  // DUB-67: once an admin enters admin-land, keep the admin nav until they
  // explicitly click "User Dashboard". Without this stickiness the nav
  // flips back to the player view as soon as they navigate to a player URL
  // (e.g. /tournaments/:id), which feels like an accidental escape hatch.
  // The flag is persisted to localStorage so it survives reloads/new tabs;
  // logout clears it via localStorage.clear() in handleLogout.
  //
  // DUB-73: The earlier version of this used a useEffect that auto-asserted
  // sticky=true whenever isOnAdminRoute && !adminSticky. That created a
  // 2-click bug on "User Dashboard": the click handler set sticky=false
  // and cleared localStorage, but React re-rendered the still-mounted admin
  // page once with the new state before the URL change reconciled — so the
  // effect saw isOnAdminRoute=true (URL still admin) && !adminSticky=true
  // and rewrote localStorage to '1'. The NEW Layout (after navigation) then
  // read the resurrected flag and stayed in admin nav. The fix moves the
  // auto-assert into the useState initializer: init runs only on mount, so
  // it can't fight an in-flight exit. Layout is per-page, so every route
  // change runs init afresh.
  const ADMIN_STICKY_KEY = 'dub_admin_mode_sticky'
  const [adminSticky, setAdminSticky] = useState(() => {
    try {
      if (isOnAdminRoute) {
        localStorage.setItem(ADMIN_STICKY_KEY, '1')
        return true
      }
      // Auto-exit sticky admin mode on ANY user-facing route (owner 2026-06-09:
      // "if we're not in the admin dashboard our experience should be exactly
      // like other users"). Sticky now only persists while actually on an admin
      // route (handled above); the moment we're on a player URL the flag is
      // cleared, so admins get the regular-user nav chrome and none of the
      // admin redirects (e.g. TournamentView's). This intentionally reverses
      // the DUB-67 stickiness that carried admin nav onto player pages. Layout
      // re-mounts per route, so this runs fresh on every navigation.
      localStorage.removeItem(ADMIN_STICKY_KEY)
      return false
    } catch {
      return isOnAdminRoute
    }
  })
  // A non-admin should never see admin nav even if a stale flag survived
  // (e.g. an admin signed out without going through handleLogout).
  const isAdminRoute = isAdmin && (isOnAdminRoute || adminSticky)

  const exitAdminMode = () => {
    setAdminSticky(false)
    try { localStorage.removeItem(ADMIN_STICKY_KEY) } catch { /* ignore */ }
  }

  const navigation = isAdminRoute
    ? [
        // forceActive: while the admin nav is visible we're somewhere in
        // admin-land — "Admin Dashboard" should always be the highlighted
        // pill so the current context is unambiguous, not just on the
        // dashboard page itself. The default isActive() check only matches
        // /admin/dashboard exactly and would leave both pills inactive on
        // every other admin route (/admin/users, /bracket/:id/seed, etc.).
        { name: 'Admin Dashboard', href: '/admin/dashboard', icon: Shield, forceActive: true },
        { name: 'User Dashboard', href: '/home', icon: LogIn, onClickExtra: exitAdminMode },
      ]
    : [
        { name: 'Home', href: '/home', icon: Home },
        { name: 'Tournaments', href: '/tournaments', icon: Trophy },
        { name: 'Leaderboards', href: '/leaderboards', icon: TrendingUp },
        { name: 'Groups', href: '/groups', icon: Users },
        // The merch storefront lives only at getdub.com/merch — from the web
        // app this leaves the app host for the apex domain (same tab); on
        // native and single-host contexts it's the internal /merch route.
        { name: 'Merch', href: storeLink(), icon: ShoppingBag },
        { name: 'Prizes', href: '/prizes', icon: Gift },
      ]

  // On the web the Dub logo at top-left exits to the public marketing
  // homepage — the app, store, and homepage are one site and the logo is the
  // way back to its front door. This leaves admin mode too, which is fine:
  // unlike the DUB-71/72 mismatch, the landing page has no app header.
  // Installed apps (native builds, home-screen PWAs) never navigate to the
  // marketing site — their logo goes to the dashboard.
  const logoHref = isInstalledApp() ? '/home' : '/'

  const isActive = (path) => path !== '' && (location.pathname === path || location.pathname.startsWith(path + '/'))

  useEffect(() => {
    const setters = new WeakMap()
    const handlers = new WeakMap()

    // RAF-based tween on CSS custom properties. Each setter interpolates from
    // the current value toward a target with a decay-per-frame that matches the
    // previous GSAP durations at ~60fps (decay = 1 - exp(-dt/tau)). This
    // replaces gsap.quickTo without adding a new animation library.
    const makeTween = (el, prop, unit, durationSec) => {
      // Per-frame smoothing factor. ~1 - exp(-1/(60*tau)) for tau ≈ duration/3.
      // Tuned empirically so motion "feels" the same as the prior GSAP tween.
      const smooth = 1 - Math.exp(-1 / (60 * (durationSec / 3)))
      let current = 0
      let target = 0
      let rafId = null
      const tick = () => {
        current += (target - current) * smooth
        if (Math.abs(target - current) < 0.001) {
          current = target
          el.style.setProperty(prop, `${current}${unit}`)
          rafId = null
          return
        }
        el.style.setProperty(prop, `${current}${unit}`)
        rafId = requestAnimationFrame(tick)
      }
      return {
        set: (v) => {
          target = v
          if (rafId === null) rafId = requestAnimationFrame(tick)
        },
        // Immediate set without tween (used for initial values).
        jump: (v) => {
          current = v
          target = v
          el.style.setProperty(prop, `${v}${unit}`)
          if (rafId !== null) {
            cancelAnimationFrame(rafId)
            rafId = null
          }
        },
        cancel: () => {
          if (rafId !== null) {
            cancelAnimationFrame(rafId)
            rafId = null
          }
        },
      }
    }

    const getSetters = (el) => {
      if (setters.has(el)) return setters.get(el)
      const setX = makeTween(el, '--glow-x', '%', 0.35)
      const setY = makeTween(el, '--glow-y', '%', 0.35)
      const setAlpha = makeTween(el, '--glow-alpha', '', 0.25)
      const setShift = makeTween(el, '--glow-shift', 'px', 0.45)
      const setScale = makeTween(el, '--glow-scale', '', 0.45)
      const isAccentGlow = el.classList.contains('card-accent-glow')
      if (isAccentGlow) {
        setX.jump(92); setY.jump(50); setAlpha.jump(0.65); setShift.jump(0); setScale.jump(1)
      } else {
        setX.jump(90); setY.jump(50); setAlpha.jump(0); setShift.jump(48); setScale.jump(0.85)
      }
      const api = {
        setX: (v) => setX.set(v),
        setY: (v) => setY.set(v),
        setAlpha: (v) => setAlpha.set(v),
        setShift: (v) => setShift.set(v),
        setScale: (v) => setScale.set(v),
        cancelAll: () => { setX.cancel(); setY.cancel(); setAlpha.cancel(); setShift.cancel(); setScale.cancel() },
      }
      setters.set(el, api)
      return api
    }

    const updateGlow = (el, event) => {
      const rect = el.getBoundingClientRect()
      const x = ((event.clientX - rect.left) / rect.width) * 100
      const y = ((event.clientY - rect.top) / rect.height) * 100
      const isAccentGlow = el.classList.contains('card-accent-glow')

      if (isAccentGlow) {
        /* Subtle translation: blend mouse position heavily with default (92, 50) so blur only moves a little */
        const glowX = 92 * 0.65 + x * 0.35
        const glowY = 50 * 0.65 + y * 0.35
        const { setX, setY, setAlpha, setShift, setScale } = getSetters(el)
        setX(glowX)
        setY(glowY)
        setAlpha(0.78)
        setShift(0)
        setScale(1)
        return
      }

      const distLeft = x
      const distRight = 100 - x
      const distTop = y
      const distBottom = 100 - y
      const minDist = Math.min(distLeft, distRight, distTop, distBottom)
      const edgeStrength = Math.max(0, 1 - minDist / 80)
      const intensity = Math.min(0.95, Math.max(0.2, edgeStrength * 1.2))
      const bias = Math.min(0.8, edgeStrength * 0.9)
      let targetX = x
      let targetY = y

      if (minDist === distLeft) {
        targetX = 8
        targetY = y
      } else if (minDist === distRight) {
        targetX = 92
        targetY = y
      } else if (minDist === distTop) {
        targetX = x
        targetY = 8
      } else {
        targetX = x
        targetY = 92
      }

      const glowX = x * (1 - bias) + targetX * bias
      const glowY = y * (1 - bias) + targetY * bias
      const { setX, setY, setAlpha, setShift, setScale } = getSetters(el)
      setX(glowX)
      setY(glowY)
      setAlpha(intensity)
      setShift(0)
      setScale(1)
    }

    const resetGlow = (el) => {
      if (!el) return
      const { setX, setY, setAlpha, setShift, setScale } = getSetters(el)
      if (el.classList.contains('card-accent-glow')) {
        setX(92)
        setY(50)
        setAlpha(0.65)
        setShift(0)
        setScale(1)
      } else {
        setX(90)
        setY(50)
        setAlpha(0)
        setShift(48)
        setScale(0.85)
      }
    }

    const attach = (el) => {
      if (handlers.has(el)) return
      const handleMove = (event) => updateGlow(el, event)
      const handleEnter = (event) => updateGlow(el, event)
      const handleLeave = () => resetGlow(el)
      el.addEventListener('pointermove', handleMove)
      el.addEventListener('pointerenter', handleEnter)
      el.addEventListener('pointerleave', handleLeave)
      handlers.set(el, { handleMove, handleEnter, handleLeave })
    }

    const detach = (el) => {
      const entry = handlers.get(el)
      if (!entry) return
      el.removeEventListener('pointermove', entry.handleMove)
      el.removeEventListener('pointerenter', entry.handleEnter)
      el.removeEventListener('pointerleave', entry.handleLeave)
      handlers.delete(el)
    }

    const syncElements = () => {
      document.querySelectorAll('.card-glass-item, .card-accent-glow').forEach((el) => {
        if (el.classList.contains('card-glass-item-no-glow')) {
          resetGlow(el)
          detach(el)
        } else {
          attach(el)
        }
      })
    }

    syncElements()

    const observer = new MutationObserver(() => {
      syncElements()
    })

    observer.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['class'] })

    return () => {
      observer.disconnect()
      document.querySelectorAll('.card-glass-item, .card-accent-glow').forEach((el) => {
        detach(el)
        const api = setters.get(el)
        if (api) api.cancelAll()
      })
    }
  }, [location.pathname])

  const handleLogout = async () => {
    try {
      // Clear all stored state first to prevent ProtectedRoute from saving stale URLs
      localStorage.removeItem('dub_intended_url')
      localStorage.clear()
      navigate('/login', { replace: true })
      await signOut()
    } catch (error) {
      console.error('Error signing out:', error)
    }
  }

  return (
    <div className="min-h-screen overflow-x-clip app-gradient">
      {/* Desktop Navigation */}
      <nav className="hidden lg:block border-b bg-[var(--mauve-1)] relative z-10 shadow-button">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center space-x-8">
              <Link to={logoHref} className="flex-shrink-0 flex items-center">
                <DubLogo className="h-8" />
              </Link>
              <div className="flex space-x-4">
                {navigation.map((item) => {
                  const Icon = item.icon
                  const commonClasses =
                    'flex items-center space-x-2 px-4 py-2 rounded-full text-sm font-medium transition-[background-color,box-shadow,transform,color] duration-200 ease-[cubic-bezier(0.25,0.1,0.25,1)]'
                  // isActive never matches absolute cross-subdomain URLs,
                  // which is right — we're not on that section's host.
                  const itemActive = item.forceActive || isActive(item.href)
                  const activeClasses = itemActive
                    ? 'bg-[color-mix(in_srgb,var(--color-purple)_5%,white)] text-[var(--color-ink)] border border-[color-mix(in_srgb,var(--color-purple)_18%,white)] shadow-nav-active translate-y-[0.4px] py-2.5 rounded-full animate-[nav-active-fade_240ms_ease]'
                    : 'text-muted-foreground hover:text-[var(--color-ink)]'

                  return (
                    <SiteLink
                      key={item.name}
                      to={item.href}
                      onClick={item.onClickExtra}
                      className={`${commonClasses} ${activeClasses}`}
                    >
                      <Icon className="h-4 w-4" />
                      <span>{item.name}</span>
                    </SiteLink>
                  )
                })}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <NotificationsDropdown />
              <UserProfileDropdown isAdmin={isAdmin} handleLogout={handleLogout} user={user} />
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Navigation - Top Bar */}
      <nav className="lg:hidden relative z-10 nav-glass">
        <div className="flex justify-between items-center h-16 px-4">
          <div className="flex-shrink-0 flex items-center">
            <Link to={logoHref} className="flex items-center">
              <DubLogo className="h-7" />
            </Link>
          </div>
          <div className="flex items-center space-x-1">
            <NotificationsDropdown />
            <UserProfileDropdown isAdmin={isAdmin} handleLogout={handleLogout} user={user} />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="border-t px-4 py-4 space-y-2">
            {navigation.map((item) => {
              const Icon = item.icon

              return (
                <SiteLink
                  key={item.name}
                  to={item.href}
                  onClick={() => {
                    if (item.onClickExtra) item.onClickExtra()
                    setMobileMenuOpen(false)
                  }}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-full text-sm font-medium w-fit transition-[background-color,box-shadow,transform,color] duration-200 ease-[cubic-bezier(0.25,0.1,0.25,1)] ${
                    item.forceActive || isActive(item.href)
                      ? 'bg-[color-mix(in_srgb,var(--color-purple)_5%,white)] text-[var(--color-ink)] border border-[color-mix(in_srgb,var(--color-purple)_18%,white)] shadow-nav-active translate-y-[0.4px] py-2.5 rounded-full animate-[nav-active-fade_240ms_ease]'
                      : 'text-muted-foreground hover:text-[var(--color-ink)]'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{item.name}</span>
                </SiteLink>
              )
            })}
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className="bg-transparent">
        {children}
      </main>
    </div>
  )
}
